import { useCallback, useMemo, useRef } from 'react'
import { useLingui } from '@lingui/react/macro'
import debounce from 'lodash.debounce'
import { NaiveDateTime, TimeUnits } from '@guestgoodie/helpers'

import DateTimeInput from '@/components/ui/DateTimeInput'
import Field from '@/components/ui/Field'
import Input from '@/components/ui/Input'

interface RecDateProps {
  startDate: NaiveDateTime
  duration: number
  showUntil: boolean
  isRecurring: boolean
  onChange: (date: NaiveDateTime, duration: number) => void
}

const HOUR_DURATION_MILLISECONDS = 60 * 60 * 1_000
export const DAY_DURATION_MILLISECONDS = 24 * HOUR_DURATION_MILLISECONDS

export function RecDate(props: RecDateProps) {
  const { startDate, duration, showUntil, isRecurring, onChange } = props

  const { t } = useLingui()

  const isWholeDay = useMemo(() => {
    const day = startDate.toDay()

    // Check that it actually starts at 0 on the day
    if (day.diff(startDate, TimeUnits.MILLISECOND) !== 0) return false

    // Check that the duration actually takes the whole day
    if (duration !== DAY_DURATION_MILLISECONDS) return false
    return true
  }, [startDate, duration])

  const endDate = useMemo(
    () => startDate.add(duration, TimeUnits.MILLISECOND),
    [startDate, duration]
  )

  const debouncedOnChange = useRef(debounce(onChange, 1000))

  const onEndDateChange = useCallback(
    (newEndDate: NaiveDateTime) => {
      if (!newEndDate) return

      const givenDuration = newEndDate.diff(startDate, TimeUnits.MILLISECOND)

      // We do this with debouncing, since it is confusing for the user when he types "13", with first the 1, which would always lead to adding +1 days
      // Normally we would do this with onBlur, but Radix Popovers and blurring aren't the best friends, so this would basically destroy the blurring.
      // There might be a better solution, let's take a look at it again in the future.
      if (startDate > newEndDate) {
        const laterEndDuration = newEndDate
          .add(1, TimeUnits.DAY)
          .diff(startDate, TimeUnits.MILLISECOND)

        debouncedOnChange.current(startDate, laterEndDuration)
      } else {
        debouncedOnChange.current.cancel()
      }

      onChange(startDate, givenDuration)
    },
    [startDate, onChange]
  )

  const onChangeWholeDay = useCallback(
    (isEnabled: boolean) => {
      if (isEnabled) {
        onChange(startDate.toDay(), DAY_DURATION_MILLISECONDS)
      } else {
        onChange(startDate, HOUR_DURATION_MILLISECONDS)
      }
    },
    [startDate, onChange]
  )

  const onChangeDay = useCallback(
    (day: string) => {
      if (!day) return

      onChange(startDate.setDate(day), duration)
    },
    [duration, startDate, onChange]
  )

  const onChangeTime = useCallback(
    (time: string) => {
      const newTime = time ? time : '00:00'

      onChange(startDate.setTime(newTime), duration)
    },
    [duration, startDate, onChange]
  )

  return (
    <div className="flex flex-col gap-1">
      <Field
        label={t({
          comment:
            'Checkbox label that asks the user if he wants to let a recurrence/availability be shown for the whole day.',
          message: `Whole day?`
        })}
        canBeDisabled
        onEnabledChange={onChangeWholeDay}
        size="small"
        isInitialEnabled={isWholeDay}
      >
        <Input
          type="date"
          className="flex-1"
          value={startDate.getDate()}
          onChange={event => onChangeDay(event.target.value)}
        />
      </Field>
      {!isWholeDay && (
        <>
          {isRecurring && (
            <>
              <Field label={t`Start on`} size="small">
                <Input
                  value={startDate.getDate()}
                  type="date"
                  onChange={event => onChangeDay(event.target.value)}
                />
              </Field>
              <Field label={showUntil ? t`From` : t`At`} size="small">
                <Input
                  type="time"
                  value={startDate.getTime()}
                  onChange={event => onChangeTime(event.target.value)}
                />
              </Field>
            </>
          )}
          {!isRecurring && (
            <Field label={showUntil ? t`From` : t`At`} size="small">
              <DateTimeInput
                value={startDate}
                onChange={date => onChange(date, duration)}
              />
            </Field>
          )}
          {showUntil && (
            <Field label={t`Until`} size="small">
              <DateTimeInput
                value={endDate}
                onChange={onEndDateChange}
                isOffset={isRecurring}
                startDate={startDate}
              />
            </Field>
          )}
        </>
      )}
    </div>
  )
}
